import * as React from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Mail, LayoutDashboard } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useConfirmCheckout } from "@/hooks/usePayments";

/**
 * Stripe success_url landing. We confirm the session server-side (which fulfils
 * the order idempotently — this is the primary path in local dev where the
 * webhook relay is unreliable), then refresh cached data so the new
 * subscription/domain appear.
 */
export function CheckoutSuccessPage() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const qc = useQueryClient();
  const confirm = useConfirmCheckout();
  const ran = React.useRef(false);

  React.useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    const finish = () => qc.invalidateQueries();

    if (sessionId) {
      confirm.mutateAsync(sessionId).then(finish).catch(finish);
    } else {
      finish();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div className="mx-auto max-w-md py-16">
      <Card>
        <CardContent className="flex flex-col items-center gap-4 py-10 text-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary">
            <CheckCircle2 className="h-7 w-7" />
          </div>
          <div>
            <h1 className="font-display text-2xl font-bold">Payment successful</h1>
            <p className="mx-auto mt-2 max-w-sm text-sm text-muted-foreground">
              Thanks for your purchase! We're setting up your services now. Your plans,
              mailboxes and remaining credits will appear in your dashboard shortly.
            </p>
          </div>

          <div className="w-full rounded-lg border border-border bg-muted/40 p-3 text-left text-sm text-muted-foreground">
            <p className="inline-flex items-center gap-1.5">
              <Mail className="h-3.5 w-3.5" />
              A confirmation email is on its way to your inbox.
            </p>
          </div>

          <Button asChild className="mt-1">
            <Link to="/dashboard">
              <LayoutDashboard className="h-4 w-4" /> Go to dashboard
            </Link>
          </Button>

          {sessionId && (
            <p className="text-[11px] text-muted-foreground">
              Ref: {sessionId.slice(0, 24)}…
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
